// src/pages/SolutionsPage.js
import React from 'react';
import { motion } from 'framer-motion';
import {
    Phone, Users, CheckCircle, Building2, Globe, BarChart3, Megaphone, BookOpen, Languages, ArrowRight
} from 'lucide-react';
import { pageVariants, pageTransition } from '../utils/pageTransitions';
import ClientLogos from '../components/ClientLogos';

const SolutionsPage = () => {
    return (
        <motion.div
            initial="initial"
            animate="in"
            exit="out"
            variants={pageVariants}
            transition={pageTransition}
            className="min-h-screen pt-20"
        >
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-5xl mx-auto">
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="text-center mb-16"
                        >
                            <h1 className="text-4xl font-bold text-gray-900 mb-4">
                                Solutions for Housing Authorities
                            </h1>
                            <p className="text-xl text-gray-600">
                                Address your biggest challenges with AI-powered resident support
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16"
                        >
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl p-8">
                                <Phone className="h-12 w-12 text-blue-600 mb-4" />
                                <h3 className="text-2xl font-bold mb-4">Cut Wait Times</h3>
                                <p className="text-gray-700 mb-4">
                                    Cut call volumes by 60% or more. Let residents get instant answers to common questions
                                    while your staff handles complex cases.
                                </p>
                                <ul className="space-y-2">
                                    <li className="flex items-start gap-2">
                                        <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                                        <span className="text-sm">24/7 availability</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                                        <span className="text-sm">No hold times</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                                        <span className="text-sm">Instant policy lookups</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-3xl p-8">
                                <Globe className="h-12 w-12 text-green-600 mb-4" />
                                <h3 className="text-2xl font-bold mb-4">Serve LEP Residents</h3>
                                <p className="text-gray-700 mb-4">
                                    Break down language barriers with support in 10+ languages. Ensure equal access to
                                    housing information for all residents.
                                </p>
                                <ul className="space-y-2">
                                    <li className="flex items-start gap-2">
                                        <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                                        <span className="text-sm">Real-time translation</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                                        <span className="text-sm">Voice support</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                                        <span className="text-sm">Cultural sensitivity</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-3xl p-8">
                                <Users className="h-12 w-12 text-purple-600 mb-4" />
                                <h3 className="text-2xl font-bold mb-4">Free Up Staff</h3>
                                <p className="text-gray-700 mb-4">
                                    Let your team focus on what matters most - helping residents with complex needs &
                                    building stronger communities.
                                </p>
                                <ul className="space-y-2">
                                    <li className="flex items-start gap-2">
                                        <CheckCircle className="h-5 w-5 text-purple-600 mt-0.5" />
                                        <span className="text-sm">Automate routine queries</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle className="h-5 w-5 text-purple-600 mt-0.5" />
                                        <span className="text-sm">Smart escalation</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle className="h-5 w-5 text-purple-600 mt-0.5" />
                                        <span className="text-sm">Staff satisfaction</span>
                                    </li>
                                </ul>
                            </div>
                        </motion.div>

                        {/* Authorities Using HousingAI */}
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.35 }}
                            className="mb-16"
                        >
                            <h3 className="text-3xl font-bold mb-8 text-center text-gray-900">Authorities Using HousingAI</h3>
                            <ClientLogos size="default" showNames={false} />
                        </motion.div>

                        {/* Translation Library Highlight */}
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.38 }}
                            className="mb-16 bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8 md:p-12"
                        >
                            <div className="grid md:grid-cols-2 gap-8 items-center">
                                <div>
                                    <div className="inline-flex items-center gap-2 bg-green-200 text-green-800 px-3 py-1 rounded-full text-sm font-semibold mb-4">
                                        <Languages className="h-4 w-4" />
                                        Translation Library
                                    </div>
                                    <h3 className="text-3xl font-bold mb-4 text-gray-900">Every Document<br />In Every Language</h3>
                                    <p className="text-gray-700 mb-4">
                                        Every public-facing page, form, and document from each housing authority &mdash; translated nightly into 10+ languages. Residents can read official HA materials in their native language, with side-by-side access to the original English source.
                                    </p>
                                    <p className="text-gray-700 mb-6">
                                        Includes applications, notices, policy documents, and official forms.
                                    </p>
                                    <a
                                        href="/translate"
                                        className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-green-700 transition-all"
                                    >
                                        Explore the Library <ArrowRight className="h-4 w-4" />
                                    </a>
                                </div>
                                <div className="bg-white rounded-2xl shadow-lg p-6">
                                    <ul className="space-y-3">
                                        <li className="flex items-start gap-3">
                                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                                            <span className="text-gray-700">Pages, forms, notices &amp; applications</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                                            <span className="text-gray-700">10+ languages, refreshed nightly</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                                            <span className="text-gray-700">Side-by-side view with original English</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                                            <span className="text-gray-700">Direct link back to the official source</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </motion.div>

                        {/* Case Study */}
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="bg-gray-50 rounded-3xl p-12"
                        >
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center justify-items-center">
                                <div className="flex justify-center lg:justify-end w-full">
                                    <img
                                        src="/salem-CHoog.webp"
                                        alt="Salem Housing Authority representative"
                                        className="rounded-2xl shadow-xl w-[279px] h-[279px] object-cover"
                                    />
                                </div>
                                <div className="flex justify-center lg:justify-start w-full">
                                    <div>
                                        <div className="flex items-center gap-2 mb-4">
                                            <Building2 className="h-8 w-8 text-blue-600" />
                                            <span className="text-xl font-semibold text-blue-600">Salem Housing Authority</span>
                                        </div>
                                        <h3 className="text-3xl font-bold mb-4">Live in Just 10 Days</h3>
                                        <p className="text-gray-600 mb-6">
                                            "The implementation was seamless. Within two weeks, our residents had 24/7 access
                                            to answers in multiple languages. Call volume dropped by 65% in the first month."
                                        </p>
                                        <p className="font-semibold text-gray-900 mb-2">
                                            - Cathy Hoog, Executive Director
                                        </p>
                                        <div className="flex gap-4 text-sm text-gray-600">
                                            <span>• 2,500 units</span>
                                            <span>• 11 languages</span>
                                            <span>• 65% call reduction</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Additional Benefits */}
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="mt-16"
                        >
                            <h3 className="text-3xl font-bold mb-12 text-center text-gray-900">Additional Benefits</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-shadow">
                                    <div className="flex items-start gap-4">
                                        <div className="bg-orange-200 rounded-full p-3">
                                            <Users className="h-6 w-6 text-orange-700" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-lg mb-2 text-gray-900">Expand Reach to Underserved Populations</h4>
                                            <p className="text-gray-700">24/7 availability and multilingual support removes barriers to accessing applications and discrimination complaint processes.</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-shadow">
                                    <div className="flex items-start gap-4">
                                        <div className="bg-teal-200 rounded-full p-3">
                                            <BarChart3 className="h-6 w-6 text-teal-700" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-lg mb-2 text-gray-900">Identify Service Gaps</h4>
                                            <p className="text-gray-700">Analytics help PHAs identify frequently asked questions, emerging concerns, and areas for improvement.</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-shadow">
                                    <div className="flex items-start gap-4">
                                        <div className="bg-pink-200 rounded-full p-3">
                                            <Megaphone className="h-6 w-6 text-pink-700" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-lg mb-2 text-gray-900">Increase Program Awareness</h4>
                                            <p className="text-gray-700">Provides information about supportive service programs like weekly food pantry or social events.</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-shadow">
                                    <div className="flex items-start gap-4">
                                        <div className="bg-indigo-200 rounded-full p-3">
                                            <BookOpen className="h-6 w-6 text-indigo-700" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-lg mb-2 text-gray-900">Stay Current with Regulations</h4>
                                            <p className="text-gray-700">Real-time updates on HUD policies including HOTMA & state rental assistance changes from EOHLC.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>
        </motion.div>
    );
};

export default SolutionsPage;

// ===== END OF src/pages/SolutionsPage.js =====